
import confetti from "canvas-confetti";
import { useEffect, useRef, useState } from "react";
import env from "shared/config/env";
import io from "socket.io-client";

const useBottleSocket = (roomCode, userName, userQuestion, drinkType) => {
    const [isJoined, setIsJoined] = useState(false);
    const [step, setStep] = useState(1);
    const [selectedMode, setSelectedMode] = useState(null);
    const [players, setPlayers] = useState([]);
    const [allQuestions, setAllQuestions] = useState([]);
    const [dareConfig, setDareConfig] = useState({});
    const [luckyUser, setLuckyUser] = useState(null);
    const [displayResult, setDisplayResult] = useState("");
    const [lastModeResult, setLastModeResult] = useState(null);
    const [isDone, setIsDone] = useState(false);
    const [spunModes, setSpunModes] = useState({ truth: false, dare: false });

    const [rotationPlayer, setRotationPlayer] = useState(0);
    const [rotationQuestion, setRotationQuestion] = useState(0);
    const [rotationDare, setRotationDare] = useState(0);

    const socketRef = useRef(null);

    const runFireworks = () => {
        const duration = 2000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };
        const randomInRange = (min, max) => Math.random() * (max - min) + min;

        const interval = setInterval(() => {
            const timeLeft = animationEnd - Date.now();
            if (timeLeft <= 0) return clearInterval(interval);

            const particleCount = 650 * (timeLeft / duration);
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
            confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
        }, 250);
    };

    useEffect(() => {
        return () => socketRef.current?.disconnect();
    }, []);

    const joinRoom = () => {
        socketRef.current = io(env.baseUrl, { transports: ["websocket"] });

        socketRef.current.on("connect", () => {
            socketRef.current.emit("join_room", {
                roomCode,
                playerName: userName,
                question: userQuestion,
                drinkType,
            });
            setIsJoined(true);
        })

        socketRef.current.on("join_error", (msg) => {
            alert(msg);
            setIsJoined(false);
        });

        socketRef.current.on("sync_dare_config", (config) => setDareConfig(config));
        socketRef.current.on("update_player_list", (list) => setPlayers(list));
        socketRef.current.on("update_question_pool", (qs) => setAllQuestions(qs));
        socketRef.current.on("force_leave_room", () => window.location.reload());

        socketRef.current.on("player_result", (data) => {
            setStep(1);
            setLuckyUser(null);
            setSelectedMode(null);
            setDisplayResult("");
            setIsDone(false);
            setSpunModes({ truth: false, dare: false });
            const luckyAngle = (data.luckyIndex / data.allPlayers.length) * 360;
            setRotationPlayer((prev) => prev + 1440 + ((luckyAngle - (prev % 360) + 360) % 360));
            setTimeout(() => {
                setLuckyUser(data.luckyPlayer);
                setStep(2);
            }, 3000);
        });

        socketRef.current.on("mode_selected", (data) => {
            setSelectedMode(data.mode);
            setStep(2);
        });

        socketRef.current.on("question_result", (data) => {
            setSpunModes((prev) => ({ ...prev, truth: true }));
            const luckyAngle = (data.luckyIndex / data.allQuestions.length) * 360;
            setRotationQuestion((prev) => prev + 1440 + ((luckyAngle - (prev % 360) + 360) % 360));
            setTimeout(() => {
                setDisplayResult(data.question);
                setLastModeResult("truth");
                setStep(3);
            }, 3000);
        });

        socketRef.current.on("dare_result", (data) => {
            setSpunModes((prev) => ({ ...prev, dare: true }));
            const luckyAngle = (data.luckyIndex / data.allOptions.length) * 360;
            setRotationDare((prev) => prev + 1440 + ((luckyAngle - (prev % 360) + 360) % 360));
            setTimeout(() => {
                setDisplayResult(data.result);
                setLastModeResult("dare");
                setStep(3);
            }, 3000);
        });

        socketRef.current.on("task_confirmed", () => {
            setIsDone(true);
            runFireworks();
        });

        socketRef.current.on("task_skipped", (data) => {
            alert(`${data.playerName} ใช้สิทธิ์ข้าม Dare!`);
            setIsDone(true);
        });
    };

    const spinPlayer = () => socketRef.current.emit("request_spin_player", roomCode);
    const selectMode = (mode) => socketRef.current.emit("select_mode", { roomCode, mode, playerId: socketRef.current.id });
    const spinQuestion = () => socketRef.current.emit("request_spin_question", roomCode);
    const spinDare = (dt) => socketRef.current.emit("request_spin_dare", { roomCode, drinkType: dt });
    const confirmTask = () => socketRef.current.emit("confirm_task_done", roomCode);
    const skipDare = () => socketRef.current.emit("use_skip_dare", roomCode);
    const addQuestion = (q) => socketRef.current.emit("add_new_question", { roomCode, question: q, playerId: socketRef.current.id });
    const resetRoom = () => socketRef.current.emit("delete_all_room_data", roomCode);

    return {
        isJoined,
        step,
        selectedMode,
        players,
        allQuestions,
        dareConfig,
        luckyUser,
        displayResult,
        lastModeResult,
        isDone,
        spunModes,
        rotationPlayer,
        rotationQuestion,
        rotationDare,
        socketRef,
        joinRoom,
        spinPlayer,
        selectMode,
        spinQuestion,
        spinDare,
        confirmTask,
        skipDare,
        addQuestion,
        resetRoom,
    };
};

export default useBottleSocket;
